import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {Fragment, useState} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";

export type CalendarViewMode = "timeline" | "timeGrid";
export type CalendarViewSpan = "day" | "week" | "month";
export type CalendarDayPart = "all_day" | "morning" | "day" | "evening" | "custom";

export interface CalendarDayPartOption {
    value: CalendarDayPart;
    label: string;
    startTime?: string;
    endTime?: string;
}

export interface CalendarViewSettings {
    mode: CalendarViewMode;
    span: CalendarViewSpan;
    stepMinutes: number;
    dayPart: CalendarDayPart;
    customStartTime: string;
    customEndTime: string;
}

export interface CalendarViewCapabilities {
    modes: readonly CalendarViewMode[];
    spans: readonly CalendarViewSpan[];
    steps: readonly number[];
    dayParts?: readonly CalendarDayPartOption[];
}

export interface CalendarViewPickerProps {
    value: CalendarViewSettings;
    capabilities: CalendarViewCapabilities;
    onChange: (value: CalendarViewSettings) => void;
}

const selectableRowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
});

const sectionLabelSx = {
    color: "text.secondary",
} as const;

const formatStep = (stepMinutes: number) => {
    if (stepMinutes < 60) return `${stepMinutes} min`;

    const hours = stepMinutes / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
};

export function CalendarViewPicker({
    value,
    capabilities,
    onChange,
}: CalendarViewPickerProps) {
    const {t} = useTranslation();
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
    const showMode = capabilities.modes.length > 1;
    const showSpan = capabilities.spans.length > 1;
    const dayParts = capabilities.dayParts ?? [];
    const showDayParts = value.span === "day" && dayParts.length > 0;
    const customRangeInvalid = Boolean(
        value.customStartTime
        && value.customEndTime
        && value.customStartTime > value.customEndTime,
    );
    const stepOptions = capabilities.steps.includes(value.stepMinutes)
        ? capabilities.steps
        : [value.stepMinutes, ...capabilities.steps];
    const rangedDayParts = dayParts.filter(option => option.startTime && option.endTime);

    const updateValue = (patch: Partial<CalendarViewSettings>) => {
        onChange({...value, ...patch});
    };

    return (
        <RightSidebarPanelButton
            icon={<VisibilityOutlinedIcon/>}
            label={t("View")}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader title={t("View")} onClose={close}/>

                    <Stack
                        gap={2}
                        sx={{px: 2, pb: 2, overflowY: "auto", minHeight: 0}}
                    >
                        {showMode && (
                            <Stack gap={0.5}>
                                <Typography variant="body2" sx={sectionLabelSx}>
                                    {t("View mode")}
                                </Typography>

                                <RadioGroup
                                    value={value.mode}
                                    onChange={event => updateValue({
                                        mode: event.target.value as CalendarViewMode,
                                    })}
                                >
                                    {capabilities.modes.map(mode => (
                                        <FormControlLabel
                                            key={mode}
                                            value={mode}
                                            control={<Radio size="small" sx={{p: 0.5, mr: 0.5}}/>}
                                            label={(
                                                <Typography variant="body1">
                                                    {t(mode === "timeline" ? "Timeline" : "Time grid")}
                                                </Typography>
                                            )}
                                            sx={selectableRowSx(value.mode === mode)}
                                        />
                                    ))}
                                </RadioGroup>
                            </Stack>
                        )}

                        {showSpan && (
                            <Stack gap={0.5}>
                                <Typography variant="body2" sx={sectionLabelSx}>
                                    {t("Time range")}
                                </Typography>

                                <Select
                                    fullWidth
                                    size="small"
                                    value={value.span}
                                    onChange={event => updateValue({
                                        span: event.target.value as CalendarViewSpan,
                                    })}
                                >
                                    {capabilities.spans.map(span => (
                                        <MenuItem key={span} value={span}>
                                            {t(`Timeline span ${span}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        )}

                        {stepOptions.length > 0 && (
                            <Stack gap={0.5}>
                                <Typography variant="body2" sx={sectionLabelSx}>
                                    {t("Timeline step")}
                                </Typography>

                                <Select
                                    fullWidth
                                    size="small"
                                    value={value.stepMinutes}
                                    onChange={event => updateValue({
                                        stepMinutes: Number(event.target.value),
                                    })}
                                >
                                    {stepOptions.map(stepMinutes => (
                                        <MenuItem key={stepMinutes} value={stepMinutes}>
                                            {formatStep(stepMinutes)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        )}

                        {showDayParts && (
                            <Stack gap={0.5}>
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <Typography variant="body2" sx={sectionLabelSx}>
                                        {t("Part of the day")}
                                    </Typography>

                                    {rangedDayParts.length > 0 && (
                                        <IconButton
                                            type="button"
                                            size="small"
                                            aria-label={t("Time filter info")}
                                            aria-expanded={Boolean(infoAnchorEl)}
                                            onClick={event => setInfoAnchorEl(event.currentTarget)}
                                            sx={{p: 0.25, color: "text.secondary"}}
                                        >
                                            <InfoOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                    )}
                                </Stack>

                                <RadioGroup
                                    value={value.dayPart}
                                    onChange={event => updateValue({
                                        dayPart: event.target.value as CalendarDayPart,
                                    })}
                                >
                                    {dayParts.map(option => {
                                        const selected = value.dayPart === option.value;
                                        const range = option.startTime && option.endTime
                                            ? ` (${option.startTime} - ${option.endTime})`
                                            : "";

                                        return (
                                            <Fragment key={option.value}>
                                                <FormControlLabel
                                                    value={option.value}
                                                    control={<Radio size="small" sx={{p: 0.5, mr: 0.5}}/>}
                                                    label={(
                                                        <Typography variant="body1">
                                                            {option.label}{range}
                                                        </Typography>
                                                    )}
                                                    sx={selectableRowSx(selected)}
                                                />

                                                {option.value === "custom" && selected && (
                                                    <Stack direction="row" gap={1} sx={{px: 0.75, pt: 1}}>
                                                        <TextField
                                                            fullWidth
                                                            type="time"
                                                            size="small"
                                                            label={t("From")}
                                                            value={value.customStartTime}
                                                            error={customRangeInvalid}
                                                            onChange={event => updateValue({
                                                                customStartTime: event.target.value,
                                                            })}
                                                            InputLabelProps={{shrink: true}}
                                                            inputProps={{max: value.customEndTime}}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            type="time"
                                                            size="small"
                                                            label={t("To")}
                                                            value={value.customEndTime}
                                                            error={customRangeInvalid}
                                                            onChange={event => updateValue({
                                                                customEndTime: event.target.value,
                                                            })}
                                                            InputLabelProps={{shrink: true}}
                                                            inputProps={{min: value.customStartTime}}
                                                        />
                                                    </Stack>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </RadioGroup>

                                <Popover
                                    open={Boolean(infoAnchorEl)}
                                    anchorEl={infoAnchorEl}
                                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                    transformOrigin={{vertical: "top", horizontal: "left"}}
                                    onClose={() => setInfoAnchorEl(null)}
                                >
                                    <Box sx={{p: 2, width: 280, maxWidth: "calc(100vw - 32px)"}}>
                                        <Typography variant="subtitle2" sx={{mb: 0.75}}>
                                            {t("Time filter info")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                            {t("Reservations are filtered based on their start time:")}
                                        </Typography>
                                        <Stack gap={0.5}>
                                            {rangedDayParts.map(option => (
                                                <Typography key={option.value} variant="body2">
                                                    <Box component="span" sx={{fontWeight: 600}}>
                                                        {option.label}:
                                                    </Box>{" "}
                                                    {option.startTime} - {option.endTime}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Popover>
                            </Stack>
                        )}
                    </Stack>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
